import { AuthenticationScheme } from '@see-power-pft-common-js/auth/enums';
import { AuthStoreAccess } from 'common-react/auth/auth-store';
import { AuthActionHooks, IAuthScheme } from 'common-react/auth/AuthProvider';
import {
  getCipm4SchemeAuthenticationJwtTokenUsingAuthorizationCode,
  getCipm4SchemeAuthenticationJwtTokenUsingRefreshToken,
  redirectToCipm4Login,
  redirectToCipm4Logout,
} from 'common-react/auth/schemes/cipm4/cipm4';
import { useCallback, useEffect, useMemo } from 'react';

const getIsCipm4Enabled = () => {
  return import.meta.env.VITE_CIPM4_ENABLED === 'true';
};

function generateCodeVerifier(length = 64): string {
  const charset =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';

  const randomValues = new Uint8Array(length);
  crypto.getRandomValues(randomValues);

  return Array.from(randomValues)
    .map((v) => charset[v % charset.length])
    .join('');
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);

  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replaceAll('=', '');
}

const tryHandleRedirectState = (redirectState: string | null) => {
  if (redirectState && redirectState !== import.meta.env.VITE_CIPM4_STATE) {
    // we have a "go back to" state=/1/optimisation for example

    // Solve for both scheme possibilities
    const scheme = `${import.meta.env.VITE_CIPM4_REDIRECT_URI}`.startsWith(
      'https://',
    )
      ? 'https://'
      : 'http://';

    const cipmRedirectUriWithoutScheme = `${
      import.meta.env.VITE_CIPM4_REDIRECT_URI
    }`
      .replace('https://', '')
      .replace('http://', '');

    // cipmRedirectUri may or may not end in a /
    // redirectState may or may not begin with a /
    // result should be a valid URI of the form cipmRedirectUri/redirectState
    const href = `${scheme}${`${cipmRedirectUriWithoutScheme}/${redirectState}`
      .replace('//', '/')
      .replace('//', '/')}`;

    window.location.href = `${href}?state=${import.meta.env.VITE_CIPM4_STATE}`;

    return true;
  }

  return false;
};

const doCipmLogin = async (
  authStoreAccess: AuthStoreAccess,
  onStartLogin: AuthActionHooks['onStartLogin'],
  invitation: string | undefined,
  passthroughParams?: Record<string, string>,
) => {
  if (authStoreAccess.getAuthenticationScheme()) {
    return;
  }

  onStartLogin();

  authStoreAccess.resetForAuthenticationScheme(AuthenticationScheme.CIPM4);

  const verifier = generateCodeVerifier(64);

  const codeChallenge = await generateCodeChallenge(verifier);

  authStoreAccess.setCodeVerifier(verifier);

  redirectToCipm4Login(invitation, codeChallenge, passthroughParams);
};

const usedAuthorizationCodes = new Set<string>();

const useCipmCallbackHandling = (props: {
  authStoreAccess: AuthStoreAccess;
  actionHooks: AuthActionHooks;
}) => {
  useEffect(() => {
    void (async () => {
      const searchParams = new URLSearchParams(window.location.search);

      const invitation = searchParams.get('invitation');

      if (invitation) {
        if (!getIsCipm4Enabled()) {
          throw new Error(
            'CIPM4 is not enabled, but invitation parameter was found in URL',
          );
        }

        // CIPM4 may send other user-specific params (e.g. ext-prompt) alongside invitation, all must round-trip back to it
        const passthroughParams = Object.fromEntries(
          [...searchParams.entries()].filter(
            ([key]) => key === 'invitation' || key.startsWith('ext-'),
          ),
        );
        await doCipmLogin(
          props.authStoreAccess,
          props.actionHooks.onStartLogin,
          invitation,
          passthroughParams,
        );

        return;
      }

      if (
        props.authStoreAccess.getAuthenticationScheme() !==
        AuthenticationScheme.CIPM4
      ) {
        return;
      }

      const redirectState = searchParams.get('state');

      if (tryHandleRedirectState(redirectState)) {
        return;
      }

      if (!redirectState) {
        if (props.authStoreAccess.getJwtToken()) {
          return;
        }

        props.actionHooks.onStartLogout();

        props.authStoreAccess.clearAuthState();

        props.actionHooks.onEndLogout();

        // eslint-disable-next-line no-console
        console.error('Handling CIPM4 redirect but state missing');

        return;
      }

      const authorizationCode = searchParams.get('code');

      if (authorizationCode) {
        if (usedAuthorizationCodes.has(authorizationCode)) {
          // prevent dev-mode double-callback issue from causing a second login attempt
          // with the same authorization code, which will fail, and cause an immediate log-out
          return;
        }

        usedAuthorizationCodes.add(authorizationCode);

        const codeVerifier = props.authStoreAccess.getCodeVerifier();
        if (!codeVerifier) {
          // eslint-disable-next-line no-console
          console.error(
            'Handling CIPM4 redirect but code verifier not found in auth store',
          );

          props.actionHooks.onStartLogout();

          props.authStoreAccess.clearAuthState();

          props.actionHooks.onEndLogout();

          return;
        }

        const newJwtToken =
          await getCipm4SchemeAuthenticationJwtTokenUsingAuthorizationCode(
            authorizationCode,
            codeVerifier,
          );

        if (newJwtToken) {
          props.authStoreAccess.setJwtToken(newJwtToken);

          props.actionHooks.onEndLogin();

          return;
        }

        // eslint-disable-next-line no-console
        console.error(
          'Handling CIPM4 redirect but failed to get JWT token using authorization code',
        );
      }

      if (redirectState === import.meta.env.VITE_CIPM4_STATE) {
        // redirected back from CIPM screens
        return;
      }

      // redirect processing failed
      props.actionHooks.onStartLogout();

      props.authStoreAccess.clearAuthState();

      props.actionHooks.onEndLogout();
    })();
  }, [props]);
};

export const useCipm4AuthScheme = (props: {
  authStoreAccess: AuthStoreAccess;
  actionHooks: AuthActionHooks;
}): IAuthScheme => {
  const isEnabled = useMemo(() => getIsCipm4Enabled(), []);

  const login = useCallback(async () => {
    await doCipmLogin(
      props.authStoreAccess,
      props.actionHooks.onStartLogin,
      undefined,
      undefined,
    );
  }, [props]);

  const logout = useCallback(async () => {
    if (!getIsCipm4Enabled()) {
      return;
    }

    if (
      props.authStoreAccess.getAuthenticationScheme() !==
      AuthenticationScheme.CIPM4
    ) {
      return;
    }

    // no possibility to register end of logout in callbacks
    props.actionHooks.onEndLogout();

    redirectToCipm4Logout();
  }, [props]);

  const refresh = useCallback(async () => {
    if (!getIsCipm4Enabled()) {
      return;
    }

    // use getAuthenticationScheme() to avoid rerender
    if (
      props.authStoreAccess.getAuthenticationScheme() !==
      AuthenticationScheme.CIPM4
    ) {
      return;
    }

    const jwtToken = props.authStoreAccess.getJwtToken();
    if (!jwtToken) {
      return;
    }

    props.actionHooks.onStartLogin();

    const payload = atob(jwtToken.split('.')[1]);

    const {
      CIPM: { refreshToken },
    } = JSON.parse(payload) as { CIPM: { refreshToken: string } };

    const newJwtToken =
      await getCipm4SchemeAuthenticationJwtTokenUsingRefreshToken(refreshToken);
    if (newJwtToken) {
      props.authStoreAccess.setJwtToken(newJwtToken);

      props.actionHooks.onEndLogin();

      return;
    }

    props.actionHooks.onStartLogout();

    props.authStoreAccess.clearAuthState();

    props.actionHooks.onEndLogout();
  }, [props]);

  useCipmCallbackHandling(props);

  return useMemo(
    () => ({ isEnabled, login, logout, refresh }),
    [isEnabled, login, logout, refresh],
  );
};
